if (!customElements.get('tf-care-products')) {
  customElements.define(
    'tf-care-products',
    class TfCareProducts extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onCartUpdate = this.onCartUpdate.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);

        if (this.dataset.sectionId && typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('change', this.onChange);
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      onCartUpdate(event) {
        if (event?.source === 'tf-care-products') return;
        this.refreshFromSection();
      }

      onChange(event) {
        const select = event.target.closest('[data-tf-care-variant]');
        if (!select || !this.contains(select)) return;

        // Dawn cart-items treats every `change` as a quantity update.
        event.stopPropagation();

        const form = select.closest('[data-tf-care-form]');
        const idInput = form?.querySelector('input[name="id"]');
        const option = select.selectedOptions[0];
        if (!form || !idInput || !option) return;

        idInput.value = select.value;

        const priceEl = form.closest('[data-tf-care-card]')?.querySelector('[data-tf-care-price]');
        if (priceEl && option.dataset.price) {
          const span = document.createElement('span');
          span.className = 'tf-care-products__price-item';
          span.textContent = option.dataset.price;
          priceEl.replaceChildren(span);
        }

        const addButton = form.querySelector('[data-tf-care-add]');
        if (addButton) {
          addButton.disabled = option.dataset.available === 'false';
        }
      }

      onClick(event) {
        const addButton = event.target.closest('[data-tf-care-add]');
        if (!addButton || !this.contains(addButton) || addButton.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        this.addToCart(addButton.closest('[data-tf-care-form]'), addButton);
      }

      sectionIdsToRequest() {
        const ids = [];
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');

        if (cartDrawer?.getSectionsToRender) {
          cartDrawer.getSectionsToRender().forEach((section) => ids.push(section.id));
        }
        if (cartNotification?.getSectionsToRender) {
          cartNotification.getSectionsToRender().forEach((section) => ids.push(section.id));
        }

        const mainCartItems = document.getElementById('main-cart-items');
        if (mainCartItems?.dataset.id) ids.push(mainCartItems.dataset.id);

        const mainCartFooter = document.getElementById('main-cart-footer');
        if (mainCartFooter?.dataset.id) ids.push(mainCartFooter.dataset.id);

        if (this.dataset.sectionId) ids.push(this.dataset.sectionId);
        if (!ids.includes('cart-icon-bubble')) ids.push('cart-icon-bubble');

        return [...new Set(ids.filter(Boolean))];
      }

      addToCart(form, submitButton) {
        if (!form || form.dataset.tfBusy === 'true') return;

        const idInput = form.querySelector('input[name="id"]');
        const variantId = idInput?.value;
        if (!variantId) return;

        const errorEl = form.querySelector('[data-tf-care-error]');
        const spinner = submitButton.querySelector('.loading__spinner');
        this.toggleError(errorEl, '');
        this.setLoading(submitButton, spinner, true);
        form.dataset.tfBusy = 'true';

        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');
        const onCartPage = Boolean(document.getElementById('main-cart-items'));
        const inDrawer = Boolean(this.closest('cart-drawer'));

        const body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: this.sectionIdsToRequest().join(','),
          sections_url: window.location.pathname,
        });

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, 1);

        fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.toggleError(errorEl, response.description || response.message || window.cartStrings?.error);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            if (cartDrawer) {
              cartDrawer.classList.remove('is-empty');
              if (inDrawer || !onCartPage) {
                cartDrawer.renderContents(response);
              } else {
                this.updateDrawerQuietly(cartDrawer, response);
              }
            } else if (cartNotification?.renderContents) {
              cartNotification.renderContents(response);
            }

            this.replaceSections(response);
            this.publishCartUpdate(variantId, response);
          })
          .catch((error) => {
            console.error(error);
            this.toggleError(errorEl, window.cartStrings?.error);
            this.dispatchCartErrorEvent(error.message || window.cartStrings?.error, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(error);
          })
          .finally(() => {
            this.setLoading(submitButton, spinner, false);
            form.dataset.tfBusy = 'false';
          });
      }

      updateDrawerQuietly(cartDrawer, parsedState) {
        if (!parsedState?.sections || !cartDrawer.getSectionsToRender) return;

        cartDrawer.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement || !parsedState.sections[section.id]) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });

        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
      }

      replaceSections(parsedState) {
        if (!parsedState?.sections) return;

        const mappings = [
          { id: 'main-cart-items', selector: '.js-contents' },
          { id: 'main-cart-footer', selector: '.js-contents' },
        ];

        mappings.forEach(({ id, selector }) => {
          const root = document.getElementById(id);
          const html = root?.dataset.id ? parsedState.sections[root.dataset.id] : null;
          if (!root || !html) return;
          const source = this.parseHTML(html).querySelector(selector);
          const target = root.querySelector(selector);
          if (source && target) target.innerHTML = source.innerHTML;
        });

        if (this.dataset.sectionId && parsedState.sections[this.dataset.sectionId]) {
          this.replaceSectionRoot(this.dataset.sectionId, parsedState.sections[this.dataset.sectionId]);
        }
      }

      refreshFromSection() {
        if (!this.dataset.sectionId || this.dataset.tfRefreshing === 'true') return Promise.resolve();
        this.dataset.tfRefreshing = 'true';

        return fetch(`${window.routes.cart_url}?section_id=${this.dataset.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            this.replaceSectionRoot(this.dataset.sectionId, responseText);
          })
          .catch((error) => console.error(error))
          .finally(() => {
            this.dataset.tfRefreshing = 'false';
          });
      }

      replaceSectionRoot(sectionId, html) {
        const wrapperId = `shopify-section-${sectionId}`;
        const currentWrapper = document.getElementById(wrapperId);
        const newWrapper = this.parseHTML(html).getElementById(wrapperId);
        if (currentWrapper && newWrapper) {
          currentWrapper.replaceWith(newWrapper);
          return;
        }

        const source = this.parseHTML(html).querySelector('tf-care-products');
        if (source) this.replaceWith(source);
      }

      publishCartUpdate(variantId, cartData) {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'tf-care-products',
          productVariantId: variantId,
          cartData,
        });
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: this.closest('cart-drawer') ? 'dialog' : 'cart',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${window.routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        return this.parseHTML(html).querySelector(selector).innerHTML;
      }

      parseHTML(html) {
        return new DOMParser().parseFromString(html, 'text/html');
      }

      setLoading(button, spinner, isLoading) {
        button.classList.toggle('loading', isLoading);
        button.toggleAttribute('aria-disabled', isLoading);
        spinner?.classList.toggle('hidden', !isLoading);
      }

      toggleError(errorEl, message) {
        if (!errorEl) return;
        errorEl.hidden = !message;
        errorEl.textContent = message || '';
      }
    }
  );
}
